import { useState, useEffect } from "react";
import React from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { backend } from "../../../../../backend";
import { useAuth } from "../../../../../context/AuthContext";
import { logAxiosError } from "../../../../../logging/error";
import RoundedButton from "../../../../../components/Buttons/RoundedButton/RoundedButton";
import "./passwordSettings.css";
import {
  validatePassword,
  validateRepeatPassword,
} from "../../../../../utils/validators";
import { Tooltip as ReactTooltip } from "react-tooltip";
import PasswordRulesModal from "../../../../../components/PasswordRulesModal/PasswordRulesModal";

const PasswordSettings = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [passwordMessage, setpasswordMessage] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [showPasswordRules, setShowPasswordRules] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const auth = useAuth();

  useEffect(() => {
    if (showPasswordRules) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "unset";
      document.body.style.touchAction = "auto";
    }

    return () => {
      document.body.style.overflow = "unset";
      document.body.style.touchAction = "auto";
    };
  }, [showPasswordRules]);

  const password = watch("password", "");

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleRepeatPasswordVisibility = () => {
    setShowRepeatPassword(!showRepeatPassword);
  };

  const togglePasswordModal = (e) => {
    setShowPasswordRules(!showPasswordRules);
  };

  const onSubmitPasswordHandler = async (formData) => {
    try {
      const password = formData.password;

      const { data } = await axios.post(backend("/api/users/update/password"), {
        token: localStorage.getItem("token"),
        password,
      });

      if (data.status) {
        localStorage.setItem("user", JSON.stringify(data.user));
        setpasswordMessage("Ditt nya lösenord har sparats");
        setIsPasswordValid(true);
      }
    } catch (error) {
      setIsPasswordValid(false);
      logAxiosError(error);
      // TODO: notify user
      if (error.response?.status === 401) {
        // invalid JWT token -> log out and return to landing page
        auth.logout();
      }
    }
  };

  const tooltipText = {
    password: "Klicka för att läsa mer om reglerna kring lösenord",
  };

  const renderTooltipIcon = (tooltipHtml) => (
    <svg
      aria-hidden="true"
      data-tooltip-id="my-tooltip"
      data-tooltip-html={tooltipHtml}
      viewBox="0 0 512 512"
      fill="currentColor"
      height="28px"
      width="28px"
    >
      <path d="M256 512c141.4 0 256-114.6 256-256S397.4 0 256 0 0 114.6 0 256s114.6 256 256 256zm-86.2-346.7c7.9-22.3 29.1-37.3 52.8-37.3h58.3c34.9 0 63.1 28.3 63.1 63.1 0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6-13.3 0-24-10.7-24-24v-13.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1 0-8.4-6.8-15.1-15.1-15.1h-58.3c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM288 352c0 17.7-14.3 32-32 32s-32-14.3-32-32 14.3-32 32-32 32 14.3 32 32z" />
    </svg>
  );

  return (
    <>
      <form
        className="settings-form"
        onSubmit={handleSubmit(onSubmitPasswordHandler)}
      >
        <h2>Ange nytt lösenord</h2>
        <div className="password-container">
          <div>
            <div className="settings-password-label-container">
              <label htmlFor="settings-password">Lösenord</label>
              <button
                onClick={togglePasswordModal}
                type="button"
                aria-label="Klicka för att läsa mer om reglerna kring lösenord"
              >
                {renderTooltipIcon(tooltipText.password)}
              </button>
            </div>

            {showPasswordRules && (
              <div
                className="modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-labelledby="password-rules-title"
              >
                <PasswordRulesModal toggleModal={togglePasswordModal} />
              </div>
            )}

            <div className="settings-password-div">
              <input
                data-testid="settings-input-password"
                className={`input-field`}
                {...register("password", { validate: validatePassword })}
                type={showPassword ? "text" : "password"}
                id="settings-password"
                placeholder="Ex: Vakansa!"
                aria-invalid={errors.password ? "true" : "false"}
                aria-describedby={
                  errors.password ? "password-error" : undefined
                }
              />

              <button
                className="show-password"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Dölj lösenord" : "Visa lösenord"}
                type="button"
              >
                <img
                  src={`/images/icons/eye_${
                    showPassword ? "visible" : "hidden"
                  }.svg`}
                  alt={showPassword ? "visible" : "hidden"}
                  aria-hidden="true"
                />
              </button>
            </div>
            {errors.password && (
              <span
                className="settings-message settings-message--error"
                id="password-error"
                role="alert"
              >
                {errors.password.message}
              </span>
            )}
          </div>

          <div>
            <div>
              <label htmlFor="settings-repeat-password">Upprepa lösenord</label>
            </div>

            <div className="settings-password-div">
              <input
                className={`input-field`}
                {...register("repeat_password", {
                  validate: (value) => validateRepeatPassword(value, password),
                })}
                data-testid="settings-input-repeat-password"
                type={showRepeatPassword ? "text" : "password"}
                id="settings-repeat-password"
                aria-invalid={errors.repeat_password ? "true" : "false"}
                aria-describedby={
                  errors.repeat_password ? "repeat-password-error" : undefined
                }
              />

              <button
                className="show-password"
                onClick={toggleRepeatPasswordVisibility}
                aria-label={showPassword ? "Dölj lösenord" : "Visa lösenord"}
                type="button"
              >
                <img
                  src={`/images/icons/eye_${
                    showRepeatPassword ? "visible" : "hidden"
                  }.svg`}
                  alt={showRepeatPassword ? "visible" : "hidden"}
                  aria-hidden="true"
                />
              </button>
            </div>
            {errors.repeat_password && (
              <span
                className="settings-message settings-message--error"
                id="repeat-password-error"
                role="alert"
              >
                {errors.repeat_password.message}
              </span>
            )}
          </div>
          {passwordMessage && (
            <p
              role="alert"
              className={
                isPasswordValid
                  ? "settings-message-status settings-message--success"
                  : "settings-message-status settings-message--error"
              }
            >
              {passwordMessage}
            </p>
          )}
        </div>
        <RoundedButton color="green" label="Spara lösenord" size="md" />
      </form>
      <ReactTooltip id="my-tooltip" className="register-tooltip" />
    </>
  );
};

export default PasswordSettings;
